import { useState, useRef, useEffect } from 'react'
import { Filter } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { VapiEvent } from './useVoiceClient'

interface Props {
  events: VapiEvent[]
}

export function VapiEventLogPanel({ events }: Props) {
  const { t } = useTranslation('calls')
  const [filter, setFilter] = useState('')
  const scrollRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const el = scrollRef.current
    if (el) el.scrollTop = el.scrollHeight
  }, [events])

  const filtered = filter
    ? events.filter((e) => e.event.toLowerCase().includes(filter.toLowerCase()))
    : events

  return (
    <div className="flex h-full flex-col overflow-hidden rounded-xl border border-border bg-surface">
      <div className="flex items-center gap-2 border-b border-border px-4 py-2.5">
        <h3 className="text-sm font-semibold text-text-primary">{t('voiceTest.events.title')}</h3>
        <div className="ml-auto flex items-center gap-1.5 rounded-lg bg-surface-elevated px-2 py-1">
          <Filter className="h-3 w-3 text-text-muted" />
          <input
            type="text"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder={t('voiceTest.events.filter')}
            className="w-24 bg-transparent text-xs text-text-primary outline-none placeholder:text-text-muted"
          />
        </div>
        <span className="text-xs tabular-nums text-text-muted">{filtered.length}</span>
      </div>
      <div ref={scrollRef} className="flex-1 overflow-y-auto font-mono text-xs">
        {filtered.length === 0 ? (
          <p className="py-8 text-center text-text-muted">
            {t('voiceTest.events.empty')}
          </p>
        ) : (
          filtered.map((entry) => (
            <div key={entry.id} className="flex gap-2 border-b border-border/50 px-3 py-1.5 hover:bg-surface-elevated/50">
              <span className="shrink-0 text-text-muted">{entry.timestamp}</span>
              <span className="shrink-0 font-semibold text-primary">{entry.event}</span>
              {entry.payload !== undefined && (
                <span className="truncate text-text-secondary">
                  {typeof entry.payload === 'string' ? entry.payload : JSON.stringify(entry.payload)}
                </span>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  )
}
